import { FC, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, FormProps, Row, Segmented } from 'antd';
import { TbArrowRight, TbEye, TbSettings } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import ConsentCard from '@/layouts/04-OAuthConsentPage/components/ConsentCard';
import { LearnMoreFooter, OurDocsLink } from '@/shared';
import { PlatformForm as Form } from '@/shared/components/forms';
import InlineIconLabel from '@/shared/components/modals/ResourceCreationModals/CreateOAuthApplicationModal/components/InlineIconLabel';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { SetStateMethod } from '@/types';

import { OAuthApplication, OAuthApplicationCreateSurface, OAuthApplicationFormValues } from '../../../../types';
import ApplicationMetadataSection from './components/ApplicationMetadataSection';
import DeveloperInformationSection from './components/DeveloperInformationSection';
import OAuthApplicationFormAnchors from './components/OAuthApplicationFormAnchors';
import OAuthApplicationFormSection from './components/OAuthApplicationFormSection';
import OAuthScopesSection from './components/OAuthScopesSection';
import OAuthSettingsSection from './components/OAuthSettingsSection';
import PublicIntegrationDetailsSection from './components/PublicIntegrationDetailsSection';
import RequiredFieldsPreviewPopover from './components/RequiredFieldsPreviewPopover';
import {
    getOAuthApplicationFormReadiness,
    RequiredOAuthApplicationField,
    requiredOAuthApplicationFieldDetails,
} from './readiness';
import { OAuthApplicationFormSectionKey } from './steps';

import './styles.css';

interface CreateOAuthApplicationProps {
    setOpen: SetStateMethod<boolean>;
    onCreated?: (oauthApplication: OAuthApplication) => void;
}

const initialValues: Partial<OAuthApplicationFormValues> = {
    allowPKCE: true,
    isPublic: false,
    redirectUris: [''],
    scopes: [],
};

const initialOpenSections: Record<OAuthApplicationFormSectionKey, boolean> = {
    metadata: true,
    publicDetails: false,
    developer: false,
    settings: false,
    scopes: false,
};

const metadataFields = new Set(['iconUrl', 'name', 'slug', 'shortDescription']);
const publicDetailsFields = new Set(['overview', 'isPublic']);
const developerFields = new Set(['developerName', 'supportEmail', 'supportURL', 'websiteUrl', 'installationUrl']);

const CreateOAuthApplication: FC<CreateOAuthApplicationProps> = ({ setOpen, onCreated }) => {
    const { token } = useAntDesignTheme();
    const [form] = Form.useForm<OAuthApplicationFormValues>();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const readinessRef = useRef(getOAuthApplicationFormReadiness(initialValues));
    const [loading, setLoading] = useState(false);
    const [surface, setSurface] = useState<OAuthApplicationCreateSurface>('configuration');
    const [openSections, setOpenSections] =
        useState<Record<OAuthApplicationFormSectionKey, boolean>>(initialOpenSections);
    const [previewValues, setPreviewValues] = useState<Partial<OAuthApplicationFormValues>>(initialValues);
    const readiness = getOAuthApplicationFormReadiness(previewValues);

    const handleCreateOAuthApplication = async (values: OAuthApplicationFormValues) => {
        try {
            setLoading(true);
            const { data } = await authorizedAxiosInstance.post<OAuthApplication>('/api/oauth-applications', values);

            toast('OAuth Application Created', { description: data.name });
            onCreated?.(data);
            setOpen(false);
        } catch (error) {
            captureException(error);
            toast.error('Unable to create OAuth application');
        } finally {
            setLoading(false);
        }
    };

    const handleValuesChange = (
        _changedValues: Partial<OAuthApplicationFormValues>,
        values: OAuthApplicationFormValues,
    ) => {
        const previousReadiness = readinessRef.current;
        const nextReadiness = getOAuthApplicationFormReadiness(values);

        setPreviewValues(values);
        setOpenSections((current) => {
            if (!nextReadiness.metadataComplete) {
                return { ...current, publicDetails: false, developer: false, settings: false, scopes: false };
            }

            return {
                ...current,
                publicDetails: !previousReadiness.metadataComplete ? true : current.publicDetails,
                developer: !previousReadiness.metadataComplete ? true : current.developer,
                settings: !previousReadiness.metadataComplete ? true : current.settings,
                scopes: nextReadiness.redirectUrisComplete
                    ? !previousReadiness.redirectUrisComplete || current.scopes
                    : false,
            };
        });
        readinessRef.current = nextReadiness;
    };

    const handleFinishFailed: FormProps<OAuthApplicationFormValues>['onFinishFailed'] = ({ errorFields }) => {
        setSurface('configuration');
        setOpenSections((current) => {
            const next = { ...current };

            errorFields.forEach(({ name }) => {
                const field = String(name[0]);

                if (metadataFields.has(field)) {
                    next.metadata = true;
                }
                if (publicDetailsFields.has(field)) {
                    next.publicDetails = true;
                }
                if (developerFields.has(field)) {
                    next.developer = true;
                }
                if (field === 'redirectUris' || field === 'allowPKCE') {
                    next.settings = true;
                }
                if (field === 'scopes') {
                    next.scopes = true;
                }
            });

            return next;
        });
    };

    const changeSurface = (nextSurface: OAuthApplicationCreateSurface) => {
        if (nextSurface === 'preview' && !readiness.previewReady) {
            return;
        }

        setPreviewValues(form.getFieldsValue(true));
        setSurface(nextSurface);
    };

    const toggleSection = (section: OAuthApplicationFormSectionKey) => {
        setOpenSections((current) => ({ ...current, [section]: !current[section] }));
    };

    const availableSteps: Record<OAuthApplicationFormSectionKey, boolean> = {
        metadata: true,
        publicDetails: readiness.metadataComplete,
        developer: readiness.metadataComplete,
        settings: readiness.metadataComplete,
        scopes: readiness.metadataComplete && readiness.redirectUrisComplete,
    };

    const handleRequiredFieldSelect = (field: RequiredOAuthApplicationField) => {
        const details = requiredOAuthApplicationFieldDetails[field];

        if (!availableSteps[details.sectionKey]) {
            return;
        }

        setSurface('configuration');
        setOpenSections((current) => ({ ...current, [details.sectionKey]: true }));

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const fieldElement = document.querySelector<HTMLElement>(`#${details.fieldId}`);

                fieldElement?.focus({ preventScroll: true });
                fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        });
    };

    const previewLabel = (
        <InlineIconLabel
            icon={<TbEye />}
            label={
                <span
                    aria-disabled={!readiness.previewReady}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', height: '100%' }}
                >
                    Preview
                    {!readiness.previewReady && (
                        <RequiredFieldsPreviewPopover
                            availableSteps={availableSteps}
                            fields={readiness.missingRequiredFields}
                            onSelect={handleRequiredFieldSelect}
                        />
                    )}
                </span>
            }
        />
    );

    return (
        <Form<OAuthApplicationFormValues>
            form={form}
            className='oauth-application-create-form'
            layout='vertical'
            requiredMark={false}
            onFinish={handleCreateOAuthApplication}
            onFinishFailed={handleFinishFailed}
            onValuesChange={handleValuesChange}
            initialValues={initialValues}
            autoComplete='off'
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            validateTrigger='onBlur'
            preserve
            style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
            <Row align='middle' style={{ marginBottom: '0.75rem' }}>
                <Col flex='none'>
                    <Segmented<OAuthApplicationCreateSurface>
                        className='oauth-application-create-segmented'
                        value={surface}
                        onChange={changeSurface}
                        options={[
                            {
                                value: 'configuration',
                                label: <InlineIconLabel icon={<TbSettings />} label='Configuration' />,
                            },
                            { value: 'preview', label: previewLabel, disabled: !readiness.previewReady },
                        ]}
                    />
                </Col>
                <Col flex='auto' />
            </Row>
            {surface === 'configuration' ? (
                <div
                    style={{
                        minHeight: 0,
                        flex: 1,
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadiusLG,
                        overflow: 'hidden',
                    }}
                >
                    <Row wrap={false} style={{ height: '100%' }}>
                        <Col
                            ref={scrollContainerRef}
                            flex='auto'
                            style={{ minWidth: 0, overflowX: 'hidden', overflowY: 'auto', scrollBehavior: 'smooth' }}
                        >
                            <OAuthApplicationFormSection
                                description='Required identity and summary information shown to users during authorization.'
                                sectionKey='metadata'
                                open={openSections.metadata}
                                onToggle={toggleSection}
                            >
                                <ApplicationMetadataSection form={form} />
                            </OAuthApplicationFormSection>
                            <OAuthApplicationFormSection
                                description={
                                    <>
                                        Optional directory information for public integrations. Learn about publishing
                                        in the <OurDocsLink text='Integration Directory' />.
                                    </>
                                }
                                sectionKey='publicDetails'
                                enabled={readiness.metadataComplete}
                                open={openSections.publicDetails}
                                onToggle={toggleSection}
                            >
                                <PublicIntegrationDetailsSection />
                            </OAuthApplicationFormSection>
                            <OAuthApplicationFormSection
                                description='Contact and support information shown on the authorization screen and integration listing.'
                                sectionKey='developer'
                                enabled={readiness.metadataComplete}
                                open={openSections.developer}
                                onToggle={toggleSection}
                            >
                                <DeveloperInformationSection />
                            </OAuthApplicationFormSection>
                            <OAuthApplicationFormSection
                                description='Configure exact callback destinations and Proof Key for Code Exchange support.'
                                sectionKey='settings'
                                enabled={readiness.metadataComplete}
                                open={openSections.settings}
                                onToggle={toggleSection}
                            >
                                <OAuthSettingsSection />
                            </OAuthApplicationFormSection>
                            <OAuthApplicationFormSection
                                description='Grant only the permissions the integration needs. Resource scopes map directly to SDK authorization.'
                                sectionKey='scopes'
                                enabled={readiness.metadataComplete && readiness.redirectUrisComplete}
                                open={openSections.scopes}
                                onToggle={toggleSection}
                            >
                                <OAuthScopesSection />
                            </OAuthApplicationFormSection>
                            <Row
                                align='middle'
                                gutter={[16, 12]}
                                style={{ padding: '1rem', borderTop: `1px solid ${token.colorBorder}` }}
                            >
                                <Col flex='auto'>
                                    <LearnMoreFooter align='left' topic='OAuth 2.0 applications' />
                                </Col>
                                <Col flex='none'>
                                    <Button
                                        type='primary'
                                        htmlType='submit'
                                        loading={loading}
                                        disabled={loading || !readiness.previewReady}
                                    >
                                        Create Application
                                        <TbArrowRight />
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                        <Col
                            flex='15rem'
                            style={{
                                padding: '1rem',
                                borderLeft: `1px solid ${token.colorBorderSecondary}`,
                                backgroundColor: token.colorBgBase,
                            }}
                        >
                            <OAuthApplicationFormAnchors
                                scrollContainerRef={scrollContainerRef}
                                availableSteps={availableSteps}
                            />
                        </Col>
                    </Row>
                </div>
            ) : (
                <div
                    style={{
                        minHeight: 0,
                        flex: 1,
                        overflow: 'hidden',
                        padding: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadiusLG,
                        backgroundColor: token.colorBgLayout,
                    }}
                >
                    <div style={{ width: 480, flex: '0 0 480px', transform: 'scale(0.8)', transformOrigin: 'center' }}>
                        <ConsentCard
                            isPreview
                            request={{ client: previewValues, requestedScopes: previewValues.scopes ?? [] }}
                        />
                    </div>
                </div>
            )}
        </Form>
    );
};

export default CreateOAuthApplication;
